/**
 * Google Merchant Center - Panel propio que sustituye al modulo
 * gmerchantcenterpro de PrestaShop.
 *
 * Pestanas:
 *  - Configuracion (umbrales, modo, custom_label rules)
 *  - Productos elegibles (filtros aplicados)
 *  - Sincronizaciones (historico + lanzar)
 *  - Estado en Merchant Center (productstatuses, diagnostics)
 *  - Mapeo de categorias PrestaShop -> Google Product Category
 *  - Cutover (switch shadow -> canonical)
 */
import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Settings, Package, RefreshCw, AlertCircle, FolderTree,
  ArrowRight, CheckCircle, XCircle, Clock, Play, Eye
} from 'lucide-react'
import { googleMerchantApi } from '@/lib/api'
import type { GMCStatus, GMCConfig } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'

const TABS = [
  { key: 'config', label: 'Configuración', icon: Settings },
  { key: 'eligible', label: 'Productos elegibles', icon: Package },
  { key: 'syncs', label: 'Sincronizaciones', icon: RefreshCw },
  { key: 'statuses', label: 'Estado en MC', icon: AlertCircle },
  { key: 'categories', label: 'Mapeo categorías', icon: FolderTree },
  { key: 'cutover', label: 'Cutover', icon: ArrowRight },
] as const

type TabKey = typeof TABS[number]['key']

const fmt = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : new Intl.NumberFormat('es-ES').format(Math.round(n))
const fmtPct = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : `${Number(n).toFixed(1)}%`

export default function GoogleMerchantPage() {
  const [tab, setTab] = useState<TabKey>('config')

  const status = useQuery({
    queryKey: ['gmc-status'],
    queryFn: googleMerchantApi.getStatus,
    refetchInterval: 30000,
  })

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <header className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Google Merchant Center</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Sincronización propia (sustituye al módulo gmerchantcenterpro)
            </p>
          </div>
          <StatusBadge status={status.data} loading={status.isLoading} />
        </div>
      </header>

      <nav className="flex flex-wrap gap-2 border-b border-gray-200 dark:border-gray-700 mb-6">
        {TABS.map(t => {
          const Icon = t.icon
          const active = tab === t.key
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={
                'px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ' +
                (active
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white')
              }
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          )
        })}
      </nav>

      <div>
        {tab === 'config' && <TabConfig />}
        {tab === 'eligible' && <TabEligible />}
        {tab === 'syncs' && <TabSyncs />}
        {tab === 'statuses' && <TabStatuses />}
        {tab === 'categories' && <TabCategories />}
        {tab === 'cutover' && <TabCutover />}
      </div>
    </div>
  )
}

// ============================================================================
// STATUS BADGE
// ============================================================================
function StatusBadge({ status, loading }: { status: GMCStatus | undefined; loading: boolean }) {
  if (loading) {
    return <span className="text-sm text-gray-500">cargando…</span>
  }
  if (!status) {
    return <span className="text-sm text-gray-500">—</span>
  }
  const ok = status.connected
  return (
    <div className="flex items-center gap-2">
      {ok ? (
        <CheckCircle className="w-5 h-5 text-emerald-600" />
      ) : (
        <XCircle className="w-5 h-5 text-red-600" />
      )}
      <div className="text-right">
        <div className="text-sm font-medium">
          {ok ? 'Conectado' : 'No conectado'}
        </div>
        <div className="text-xs text-gray-500">
          {status.merchant_id ? `Merchant ${status.merchant_id}` : 'sin merchant ID'}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// TAB: CONFIG
// ============================================================================
function TabConfig() {
  const toast = useToast()
  const qc = useQueryClient()
  const config = useQuery({ queryKey: ['gmc-config'], queryFn: googleMerchantApi.getConfig })
  const [edits, setEdits] = useState<Partial<GMCConfig>>({})
  const cur = { ...(config.data?.config || {}), ...edits } as GMCConfig

  const save = useMutation({
    mutationFn: (updates: Partial<GMCConfig>) => googleMerchantApi.updateConfig(updates),
    onSuccess: () => {
      toast.success('Configuración guardada')
      setEdits({})
      qc.invalidateQueries({ queryKey: ['gmc-config'] })
    },
    onError: (e: any) => toast.error(`Error: ${e.message}`),
  })

  if (config.isLoading) return <p className="text-sm text-gray-500">Cargando…</p>
  if (!config.data) return null

  const dirty = Object.keys(edits).length > 0

  return (
    <div className="space-y-6">
      <section className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <h3 className="font-semibold mb-3">Filtros de inclusión</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <NumberField
            label="Margen mínimo (%)"
            value={cur.filter_min_margin_percent}
            onChange={v => setEdits({ ...edits, filter_min_margin_percent: v })}
          />
          <ToggleField
            label="Requiere stock propio Factusol > 0"
            value={!!cur.filter_require_factusol_stock}
            onChange={v => setEdits({ ...edits, filter_require_factusol_stock: v ? 1 : 0 })}
          />
          <NumberField
            label="ID mínimo de imagen limpia"
            value={cur.filter_min_clean_image_id}
            onChange={v => setEdits({ ...edits, filter_min_clean_image_id: v })}
            help="Solo imágenes con id_image > este valor son consideradas 'limpias'"
          />
        </div>
      </section>

      <section className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <h3 className="font-semibold mb-3">Países activos</h3>
        <div className="flex gap-2 flex-wrap">
          {['ES', 'PT', 'FR'].map(c => {
            const enabled = (cur.enabled_countries || []).includes(c)
            return (
              <button
                key={c}
                onClick={() => {
                  const list = new Set(cur.enabled_countries || [])
                  enabled ? list.delete(c) : list.add(c)
                  setEdits({ ...edits, enabled_countries: Array.from(list) as string[] })
                }}
                className={
                  'px-4 py-2 rounded-md border text-sm font-medium ' +
                  (enabled
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600')
                }
              >
                {c}
              </button>
            )
          })}
        </div>
      </section>

      <section className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <h3 className="font-semibold mb-3">Custom labels (bidding strategy)</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <ArrayField
            label="Tramos de precio (€)"
            value={cur.price_band_breaks}
            onChange={v => setEdits({ ...edits, price_band_breaks: v })}
          />
          <ArrayField
            label="Tramos de margen (%)"
            value={cur.margin_tier_breaks}
            onChange={v => setEdits({ ...edits, margin_tier_breaks: v })}
          />
          <ArrayField
            label="Tramos de AdScore"
            value={cur.adscore_bucket_breaks}
            onChange={v => setEdits({ ...edits, adscore_bucket_breaks: v })}
          />
        </div>
      </section>

      <section className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <h3 className="font-semibold mb-3">Modo</h3>
        <div className="flex items-center gap-3">
          <div className="text-sm">
            Actual: <span className="font-medium">{cur.mode}</span>
          </div>
          <p className="text-xs text-gray-500">
            El modo se cambia desde la pestaña "Cutover" (requiere guardas de paridad).
          </p>
        </div>
      </section>

      <div className="flex justify-end gap-2">
        <button
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate(edits)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md font-medium disabled:opacity-50 hover:bg-blue-700"
        >
          {save.isPending ? 'Guardando…' : 'Guardar cambios'}
        </button>
        <button
          disabled={!dirty}
          onClick={() => setEdits({})}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-200 disabled:opacity-50"
        >
          Descartar
        </button>
      </div>
    </div>
  )
}

function NumberField({ label, value, onChange, help }: { label: string; value: number; onChange: (v: number) => void; help?: string }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
      <input
        type="number"
        value={value ?? 0}
        onChange={e => onChange(Number(e.target.value))}
        className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
      />
      {help && <span className="text-xs text-gray-500 mt-1 block">{help}</span>}
    </label>
  )
}

function ToggleField({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <input type="checkbox" checked={value} onChange={e => onChange(e.target.checked)} className="w-4 h-4 rounded border-gray-300" />
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
    </label>
  )
}

function ArrayField({ label, value, onChange }: { label: string; value: number[]; onChange: (v: number[]) => void }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
      <input
        type="text"
        value={(value || []).join(',')}
        onChange={e => onChange(e.target.value.split(',').map(s => Number(s.trim())).filter(n => !Number.isNaN(n)))}
        className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
        placeholder="40,80,150"
      />
    </label>
  )
}

// ============================================================================
// TAB: ELIGIBLE
// ============================================================================
function TabEligible() {
  const [offset, setOffset] = useState(0)
  const limit = 100
  const elig = useQuery({
    queryKey: ['gmc-eligible', offset],
    queryFn: () => googleMerchantApi.listEligible(limit, offset),
    placeholderData: prev => prev,
  })

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex items-baseline gap-3">
          <span className="text-3xl font-bold">{fmt(elig.data?.total)}</span>
          <span className="text-sm text-gray-500">productos pasan los filtros</span>
        </div>
        {elig.data?.by_family && Object.keys(elig.data.by_family).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            {Object.entries(elig.data.by_family).map(([fam, n]) => (
              <span key={fam} className="px-2 py-1 rounded bg-gray-100 dark:bg-gray-700">
                {fam}: <b>{fmt(n as number)}</b>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-700 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">id_product</th>
              <th className="px-3 py-2 text-left">reference</th>
              <th className="px-3 py-2 text-right">margen %</th>
              <th className="px-3 py-2 text-right">AdScore</th>
              <th className="px-3 py-2 text-right">precio (s/IVA)</th>
              <th className="px-3 py-2 text-right">stock Fact.</th>
              <th className="px-3 py-2 text-right">clean image</th>
              <th className="px-3 py-2 text-center">acción</th>
            </tr>
          </thead>
          <tbody>
            {(elig.data?.items || []).map(p => (
              <tr key={p.id_product} className="border-t border-gray-100 dark:border-gray-700">
                <td className="px-3 py-2">{p.id_product}</td>
                <td className="px-3 py-2 font-mono text-xs">{p.reference}</td>
                <td className="px-3 py-2 text-right">{fmtPct(p.margin_percent)}</td>
                <td className="px-3 py-2 text-right">{fmt(p.ad_score)}</td>
                <td className="px-3 py-2 text-right">{Number(p.price_ex_vat || 0).toFixed(2)} €</td>
                <td className="px-3 py-2 text-right">{fmt(p.stock_actual)}</td>
                <td className="px-3 py-2 text-right text-xs text-gray-500">{p.clean_idi}</td>
                <td className="px-3 py-2 text-center">
                  <PreviewButton idProduct={p.id_product} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between text-sm">
        <button
          disabled={offset === 0}
          onClick={() => setOffset(Math.max(0, offset - limit))}
          className="px-3 py-1 border rounded disabled:opacity-50"
        >
          ‹ Anterior
        </button>
        <span className="text-gray-500">
          {offset + 1} - {Math.min(offset + limit, elig.data?.total || 0)} de {fmt(elig.data?.total)}
        </span>
        <button
          disabled={(elig.data?.total || 0) <= offset + limit}
          onClick={() => setOffset(offset + limit)}
          className="px-3 py-1 border rounded disabled:opacity-50"
        >
          Siguiente ›
        </button>
      </div>
    </div>
  )
}

function PreviewButton({ idProduct }: { idProduct: number }) {
  const [open, setOpen] = useState(false)
  const preview = useQuery({
    queryKey: ['gmc-preview', idProduct],
    queryFn: () => googleMerchantApi.previewProduct(idProduct, 'ES', 'es', 'shadow'),
    enabled: open,
  })
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="px-2 py-1 text-xs rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 inline-flex items-center gap-1"
      >
        <Eye className="w-3 h-3" /> Preview
      </button>
      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg max-w-3xl w-full max-h-[80vh] overflow-auto p-6"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="font-semibold mb-2">Product resource (ES/es, shadow)</h3>
            {preview.isLoading && <p className="text-sm text-gray-500">Construyendo…</p>}
            {preview.error && <p className="text-sm text-red-600">Error: {String(preview.error)}</p>}
            {preview.data && (
              <pre className="text-xs font-mono bg-gray-50 dark:bg-gray-900 p-3 rounded overflow-auto">
                {JSON.stringify(preview.data, null, 2)}
              </pre>
            )}
            <div className="text-right mt-3">
              <button onClick={() => setOpen(false)} className="px-3 py-1 text-sm border rounded">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ============================================================================
// TAB: SYNCS
// ============================================================================
function TabSyncs() {
  const toast = useToast()
  const qc = useQueryClient()
  const log = useQuery({
    queryKey: ['gmc-sync-log'],
    queryFn: () => googleMerchantApi.syncLog(30),
    refetchInterval: 10000,
  })
  const [running, setRunning] = useState(false)

  const dryRun = useMutation({
    mutationFn: () => googleMerchantApi.syncDryRun({ mode: 'shadow', scope: 'full' }),
    onMutate: () => setRunning(true),
    onSettled: () => { setRunning(false); qc.invalidateQueries({ queryKey: ['gmc-sync-log'] }) },
    onSuccess: (d: any) => toast.success(`Dry-run OK: ${d.processed} payloads construidos`),
    onError: (e: any) => toast.error(`Error: ${e.message}`),
  })

  const run = useMutation({
    mutationFn: () => googleMerchantApi.syncRun({ mode: 'shadow', scope: 'full' }),
    onSuccess: () => {
      toast.success('Sincronización lanzada en segundo plano')
      qc.invalidateQueries({ queryKey: ['gmc-sync-log'] })
    },
    onError: (e: any) => toast.error(`Error: ${e.message}`),
  })

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <button
          onClick={() => dryRun.mutate()}
          disabled={running || dryRun.isPending}
          className="px-4 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md font-medium flex items-center gap-2 disabled:opacity-50"
        >
          <Eye className="w-4 h-4" /> Dry-run (no toca Google)
        </button>
        <button
          onClick={() => run.mutate()}
          disabled={run.isPending}
          className="px-4 py-2 bg-blue-600 text-white rounded-md font-medium flex items-center gap-2 disabled:opacity-50"
        >
          <Play className="w-4 h-4" /> Lanzar sincronización
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-700 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">inicio</th>
              <th className="px-3 py-2 text-left">fin</th>
              <th className="px-3 py-2 text-left">modo</th>
              <th className="px-3 py-2 text-left">scope</th>
              <th className="px-3 py-2 text-right">proc.</th>
              <th className="px-3 py-2 text-right">ok</th>
              <th className="px-3 py-2 text-right">err</th>
            </tr>
          </thead>
          <tbody>
            {(log.data?.items || []).map(it => (
              <tr key={it.id} className="border-t border-gray-100 dark:border-gray-700">
                <td className="px-3 py-2 font-mono text-xs">{it.started_at}</td>
                <td className="px-3 py-2 font-mono text-xs">{it.ended_at || <Clock className="w-3 h-3 inline" />}</td>
                <td className="px-3 py-2">{it.mode}</td>
                <td className="px-3 py-2">{it.scope}</td>
                <td className="px-3 py-2 text-right">{fmt(it.processed)}</td>
                <td className="px-3 py-2 text-right text-emerald-600 font-medium">{fmt(it.ok)}</td>
                <td className="px-3 py-2 text-right text-red-600 font-medium">{fmt(it.errors)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {(log.data?.items || []).length === 0 && (
          <p className="text-sm text-gray-500 text-center py-8">Aún no hay ejecuciones</p>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// TAB: STATUSES
// ============================================================================
function TabStatuses() {
  const diag = useQuery({ queryKey: ['gmc-diagnostics'], queryFn: googleMerchantApi.diagnostics, refetchInterval: 60000 })
  return (
    <div className="space-y-4">
      {diag.isLoading && <p className="text-sm text-gray-500">Cargando…</p>}
      {diag.data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {(diag.data.accountLevelIssues || []).slice(0, 6).map((d: any, i: number) => {
              const st = d.statistics || {}
              return (
                <div key={i} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <div className="text-xs text-gray-500">{d.destination}/{d.country}</div>
                  <div className="mt-2 text-2xl font-bold text-emerald-600">{fmt(st.active)}</div>
                  <div className="text-xs text-gray-500">activos</div>
                  <div className="mt-2 grid grid-cols-3 text-xs">
                    <div><b className="text-orange-600">{fmt(st.pending)}</b> pend</div>
                    <div><b className="text-red-600">{fmt(st.disapproved)}</b> rech</div>
                    <div><b>{fmt(st.expiring)}</b> exp</div>
                  </div>
                </div>
              )
            })}
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-4 py-3 border-b font-semibold">Top issues</div>
            <table className="w-full text-sm">
              <tbody>
                {(diag.data.top_item_issues || []).map((it: any, i: number) => (
                  <tr key={i} className="border-t border-gray-100 dark:border-gray-700">
                    <td className="px-3 py-2">{it.description}</td>
                    <td className="px-3 py-2 text-right font-medium">{fmt(it.items)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

// ============================================================================
// TAB: CATEGORIES
// ============================================================================
function TabCategories() {
  const toast = useToast()
  const qc = useQueryClient()
  const data = useQuery({ queryKey: ['gmc-cats'], queryFn: googleMerchantApi.getCategoryMap })
  const upsert = useMutation({
    mutationFn: (row: any) => googleMerchantApi.upsertCategoryMap(row),
    onSuccess: () => { toast.success('Mapeo guardado'); qc.invalidateQueries({ queryKey: ['gmc-cats'] }) },
    onError: (e: any) => toast.error(`Error: ${e.message}`),
  })

  return (
    <div className="space-y-6">
      <section>
        <h3 className="font-semibold mb-2">Categorías sin mapear ({data.data?.unmapped.length || 0})</h3>
        {(data.data?.unmapped || []).length === 0 && (
          <p className="text-sm text-gray-500">Todas las categorías con productos activos están mapeadas ✓</p>
        )}
        <div className="space-y-2">
          {(data.data?.unmapped || []).map(u => (
            <CategoryEditor
              key={u.id_category}
              idCategory={u.id_category}
              name={u.name}
              productCount={u.product_count}
              onSave={(google_category_id, google_product_type) =>
                upsert.mutate({ id_category: u.id_category, google_category_id, google_product_type })
              }
            />
          ))}
        </div>
      </section>
      <section>
        <h3 className="font-semibold mb-2">Mapeo actual ({data.data?.mapping.length || 0})</h3>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left">id_category</th>
                <th className="px-3 py-2 text-left">nombre</th>
                <th className="px-3 py-2 text-right">Google ID</th>
                <th className="px-3 py-2 text-left">product type</th>
              </tr>
            </thead>
            <tbody>
              {(data.data?.mapping || []).map(m => (
                <tr key={m.id_category} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="px-3 py-2">{m.id_category}</td>
                  <td className="px-3 py-2">{m.category_name || '—'}</td>
                  <td className="px-3 py-2 text-right font-mono">{m.google_category_id ?? '—'}</td>
                  <td className="px-3 py-2 text-xs">{m.google_product_type || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}

function CategoryEditor({ idCategory, name, productCount, onSave }: {
  idCategory: number; name: string; productCount: number;
  onSave: (gid: number, ptype: string) => void
}) {
  const [gid, setGid] = useState('')
  const [ptype, setPtype] = useState('')
  return (
    <div className="bg-white dark:bg-gray-800 rounded-md p-3 border border-gray-200 dark:border-gray-700 flex flex-col md:flex-row md:items-center gap-2">
      <div className="flex-1">
        <div className="text-sm font-medium">{name}</div>
        <div className="text-xs text-gray-500">id_category={idCategory} · {productCount} productos activos</div>
      </div>
      <input
        type="number"
        placeholder="Google Category ID"
        value={gid}
        onChange={e => setGid(e.target.value)}
        className="px-2 py-1 text-sm border rounded w-44 bg-white dark:bg-gray-700"
      />
      <input
        type="text"
        placeholder="Product type (label)"
        value={ptype}
        onChange={e => setPtype(e.target.value)}
        className="px-2 py-1 text-sm border rounded w-64 bg-white dark:bg-gray-700"
      />
      <button
        disabled={!gid}
        onClick={() => onSave(Number(gid), ptype)}
        className="px-3 py-1 text-sm bg-blue-600 text-white rounded disabled:opacity-50"
      >
        Guardar
      </button>
    </div>
  )
}

// ============================================================================
// TAB: CUTOVER
// ============================================================================
function TabCutover() {
  const toast = useToast()
  const config = useQuery({ queryKey: ['gmc-config'], queryFn: googleMerchantApi.getConfig })
  const tryCutover = useMutation({
    mutationFn: (confirm: boolean) => googleMerchantApi.cutover(confirm),
    onSuccess: (d: any) => {
      if (d.ok) toast.success('Cutover ejecutado')
      else toast.error(d.reason || 'No autorizado')
    },
    onError: (e: any) => toast.error(`Error: ${e.message}`),
  })

  const mode = config.data?.config.mode || 'shadow'

  return (
    <div className="space-y-4">
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
        <h3 className="font-semibold text-amber-900 dark:text-amber-100">Modo actual: {mode}</h3>
        <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
          {mode === 'shadow'
            ? 'En modo shadow nuestro feed coexiste con el del módulo gmerchantcenterpro pero con offerIds prefijados (JVS-NEW-) que no chocan. El módulo sigue siendo el feed primario en Merchant Center.'
            : 'En modo canonical nuestro feed es el principal. El cron del módulo PrestaShop debe estar pausado.'}
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <h3 className="font-semibold mb-2">Cutover a canonical</h3>
        <p className="text-sm text-gray-500 mb-3">
          Requiere ≥{config.data?.config.min_parity_pct_for_cutover}% de paridad de aprobaciones entre el feed shadow y el del módulo.
        </p>
        <button
          onClick={() => tryCutover.mutate(true)}
          disabled={mode === 'canonical' || tryCutover.isPending}
          className="px-4 py-2 bg-red-600 text-white rounded-md font-medium disabled:opacity-50"
        >
          Switch shadow → canonical
        </button>
      </div>
    </div>
  )
}
